import React, { useState, useEffect, useRef } from "react";
import { FaHashtag, FaAt } from "react-icons/fa";
import { VscListSelection } from "react-icons/vsc";
import { TbCircleArrowRight } from "react-icons/tb";
import { CgCalendarDates } from "react-icons/cg";
import JiraIssueModal from "./JiraIssueModal";

const DynamicJiraIssueTable = ({ issues = [], openModal, closeModal }) => {
  const tableColumns = [
    "issueType",
    "issue_key",
    "summary",
    "status",
    "priority",
    "assignee",
    "epicName",
    "reporter",
    "duedate",
    "issue_id",
    "project_key",
    "project_id",
    "parent",
    "resolution",
    "storyPoints",
    "urgency",
    "automationStatus",
    "epicLink",
    "environment",
    "prediction",
    "businessValue",
    "resource_id"
  ];

  const getColumnTitle = (key) => {
    const titleMap = {
      "issue_key": { title: "Issue Key", icon: <FaHashtag /> },
      "summary": { title: "Summary", icon: <VscListSelection /> },
      "issueType": { title: "Issue Type", icon: "" },
      "status": { title: "Status", icon: <TbCircleArrowRight /> },
      "priority": { title: "Priority", icon: <FaHashtag /> },
      "assignee": { title: "Assignee", icon: <FaAt className="text-gray-500" /> },
      "epicName": { title: "Epic Name", icon: <FaHashtag /> },
      "reporter": { title: "Reporter", icon: <FaHashtag /> },
      "duedate": { title: "Due Date", icon: <CgCalendarDates className="text-gray-500" /> },
      "issue_id": { title: "Issue ID", icon: <FaHashtag /> },
      "project_key": { title: "Project Key", icon: <FaHashtag /> },
      "project_id": { title: "Project ID", icon: <FaHashtag /> },
      "parent": { title: "Parent", icon: <FaHashtag /> },
      "resolution": { title: "Resolution", icon: <FaHashtag /> },
      "storyPoints": { title: "Story Points", icon: <FaHashtag /> },
      "urgency": { title: "Urgency", icon: <FaHashtag /> },
      "automationStatus": { title: "Automation Status", icon: <FaHashtag /> },
      "epicLink": { title: "Epic Link", icon: <FaHashtag /> },
      "environment": { title: "Environment", icon: <FaHashtag /> },
      "prediction": { title: "Prediction", icon: <FaHashtag /> },
      "businessValue": { title: "Business Value", icon: <FaHashtag /> },
      "resource_id": { title: "Resource ID", icon: <FaHashtag /> }
    };
    return titleMap[key] || { title: key, icon: null };
  };

  const getTopFields = (issues) => {
    const fields = [];
    const keysRendered = [];


    for (let i = 0; i < tableColumns.length; i++) {
      const key = tableColumns[i];

      for (let j = 0; j < issues.length; j++) {
        if (issues[j][key]) {
          keysRendered.push(key);
          break;
        }
      }

      if (keysRendered.length === 5) break;
    }


    const firstIssue = issues[0] || {};
    for (let i = 0; i < keysRendered.length; i++) {
      fields.push(firstIssue[keysRendered[i]]);
    }

    return { fields, keysRendered };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",   // "Mon"
      year: "numeric",   // "2025"
      month: "short",     // "Feb"
      day: "numeric",    // "20"
    });
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case "Critical":
        return "bg-red-50 text-red-500";
      case "High":
        return "bg-orange-50 text-orange-500";
      case "Medium":
        return "bg-yellow-50 text-yellow-500";
      case "Low":
        return "bg-green-50 text-green-500";
      default:
        return "bg-gray-50 text-gray-500";
    }
  };
  const { keysRendered } = getTopFields(issues);

  return (
    <>
      {/* DESKTOP View */}
      <table className="hidden md:block min-w-full table-auto text-sm text-left">
        <thead>
          <tr className="bg-zinc-100"  >
            {issues.length > 0 && keysRendered.map((key, index) => {
              const { title, icon } = getColumnTitle(key);
              return (
                <th className="px-3 py-2 font-medium text-gray-700 border border-gray-200" key={index}>
                  <div className="flex gap-2 items-center ">
                    {icon}
                    {title}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {issues.map((issue, index) => (
            <tr
              key={issue.id || index}
              className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200 ease-in-out cursor-pointer"
              onClick={() => openModal(issue)}
            >
              {keysRendered.map((key, i) => {
                const field = issue[key];

                if (key === 'issueType') {
                  return (
                    <td className="px-3 py-2 border border-gray-200" key={i}>
                      <div className="flex justify-center w-full">
                        <img
                          src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.issueType?.toLowerCase()}.png`}
                          onError={(e) => (e.target.src = "/assets/jiraissuetable/jiraIssueTypeIcons/default.png")}
                          alt={`${issue.issueType} Icon`}
                          className="w-4 h-4"
                        />
                      </div>
                    </td>
                  );
                }

                if (key === 'status') {
                  return (
                    <td className="px-3 py-2 whitespace-nowrap border border-gray-200" key={i}>
                      <span className={`px-2 py-1 rounded-xl text-xs font-semibold ${issue.status === "To Do" ? "bg-blue-50 text-[#5652CF]" :
                        issue.status === "In Progress" ? "bg-yellow-50 text-yellow-500" :
                          "bg-green-50 text-green-500"
                        }`}>
                        {issue.status}
                      </span>
                    </td>
                  );
                }

                if (key === 'priority') {
                  return (
                    <td className="px-3 py-2 whitespace-nowrap border border-gray-200" key={i}>
                      <span className={`px-2 py-1 rounded-xl text-xs font-semibold ${getPriorityStyles(issue.priority)}`}>
                        {issue.priority}
                      </span>
                    </td>
                  );
                }

                if (key === 'duedate') {
                  return (
                    <td className="px-3 py-2 border border-gray-200 text-gray-800" key={i}>
                      {field ? formatDate(field) : "N/A"}
                    </td>
                  );
                }

                if (key === 'summary') {
                  return (
                    <td className="px-3 py-2 border border-gray-200 text-gray-800" key={i}>
                      {field?.length > 50 ? `${field.substring(0, 50)}...` : field}
                    </td>
                  );
                }

                return (
                  <td className="px-3 py-2 whitespace-nowrap text-gray-800 border border-gray-200" key={i}>
                    {field ?? "--"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile View */}
      <div className="space-y-2 p-2 bg-zinc-700 sh md:hidden">
        {issues.map((issue, index) => (
          <div
            key={issue.id || index}
            className="bg-white rounded-lg shadow border border-gray-200 p-3"
            onClick={() => openModal(issue)}
          >
            {/* Top row: Icon + Issue Key + Status + Priority */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                {keysRendered.includes('issueType') && (
                  <img
                    src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.issueType?.toLowerCase()}.png`}
                    onError={(e) => (e.target.src = "/assets/jiraissuetable/jiraIssueTypeIcons/default.png")}
                    alt={`${issue.issueType} Icon`}
                    className="w-4 h-4"
                  />
                )}
                {keysRendered.includes('issue_key') && (
                  <span className="text-sm font-semibold text-gray-800">{issue.issue_key}</span>
                )}
              </div>
              <div className="flex space-x-2">
                {keysRendered.includes('status') && (
                  <span
                    className={`px-2 py-1 rounded-xl text-xs font-semibold ${issue.status === "To Do"
                      ? "bg-blue-50 text-[#5652CF]"
                      : issue.status === "In Progress"
                        ? "bg-yellow-50 text-yellow-500"
                        : "bg-green-50 text-green-500"
                      }`}
                  >
                    {issue.status}
                  </span>
                )}
                {keysRendered.includes('priority') && (
                  <span
                    className={`px-2 py-1 rounded-xl text-xs font-semibold ${getPriorityStyles(issue.priority)}`}
                  >
                    {issue.priority}
                  </span>
                )}
              </div>
            </div>
            {keysRendered.includes('summary') && (
              <div className="text-sm text-gray-800 mb-2">
                {issue.summary?.length > 80 ? `${issue.summary.slice(0, 80)}...` : issue.summary}
              </div>
            )}

            {/* Other fields in card body */}
            <div className="space-y-1">
              {keysRendered.map((key) => {
                if (['issueType', 'issue_key', 'status', 'priority', 'summary'].includes(key)) return null; // Already rendered above

                const field = issue[key];
                const { title } = getColumnTitle(key);
                console.log("key", key, "field", field, "title", title);

                return (
                  <div key={key} className="text-sm text-gray-700">
                    <strong>{title}:</strong>{" "}
                    {key === 'duedate'
                      ? field
                        ? formatDate(field)
                        : 'N/A'
                      : typeof field === 'string' && field.length > 80
                        ? `${field.slice(0, 80)}...`
                        : field ?? '--'}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
    // </div>
  );
};

// export default DynamicJiraIssueTable;
const DynamicJiraIssueTableWrapper = ({ issues = [] }) => {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Rename isHovered to isScaled for clarity.
  const [isScaled, setIsScaled] = useState(false);
  const scaledRef = useRef(null);

  const openModal = (issue) => {
    setSelectedIssue(issue);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedIssue(null);
    setIsModalOpen(false);
  };

  // Attach a click listener to detect clicks outside the scaled component.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scaledRef.current && !scaledRef.current.contains(event.target)) {
        setIsScaled(false);
      }
    };

    if (isScaled) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isScaled]);

  return (
    <div className="pt-0 flex items-center w-full justify-start gap-2">
      <div className="sm:block hidden w-10"></div>
      <div className="max-md:max-h-[60vh] md:max-h-[300px] flex w-full sm:w-[88%] bg-white rounded-xl items-center overflow-clip justify-center mb-4">

        <div
          onClick={() => setIsScaled(true)}
          className={`max-md:max-h-[60vh] md:max-h-[300px] w-full items-center justify-center overflow-x-auto scrollbar-none overflow-y-auto bg-white shadow-md rounded-xl border border-gray-300 
        transition-all duration-300 ease-in-out transform cursor-pointer
        ${isScaled ? "opacity-0" : "opacity-100"}`}
        >
          <div className="pointer-events-none" >
            <DynamicJiraIssueTable issues={issues} openModal={openModal} closeModal={closeModal} />
          </div>
        </div>

        {isScaled && (
          <div

            className="fixed inset-0 max-sm:p-3 sm:mr-10 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50 transition-all duration-600 ease-in-out transform "

          >
            <div
              ref={scaledRef}

              className="max-h-[600px] items-center overflow-hidden bg-white rounded-xl justify-center w-full sm:w-[60%] "
            >
              <div
                className="max-h-[600px] items-center justify-center overflow-x-auto overflow-y-auto scrollbar-none bg-white shadow-md rounded-xl border border-gray-300 transition-all duration-300 ease-in-out"
              >
                <DynamicJiraIssueTable issues={issues} openModal={openModal} closeModal={closeModal} />
              </div>
            </div>

          </div>
        )
        }

        {isModalOpen && (
          <JiraIssueModal
            issue_key={selectedIssue.issue_key}
            onClose={closeModal}
          />
        )}
      </div>
    </div>
  )
}

export default DynamicJiraIssueTableWrapper